import { useMemo } from 'react'
import { Subtitle, Title } from '@app/components/core/typography/Title'
import { PageLayout } from '@app/pages/layout/PageLayout'
import Column from '@app/components/layout/Column'
import { useTranslations } from '@i18n/useLanguage'
import { Text } from '@app/components/core/typography/Text'
import { useQuery } from '@based/sdk/react'
import useMediaQuery from '@app/util/useMediaQuery'
import { PageBreadcrumbs } from '@app/components/blocks/pageBreadcrumbs/PageBreadcrumbs'
import { TabContent } from './sections/TabContent'
import { RightColumn } from './sections/RightColumn'
import { DiscoverPageLayout } from '@app/pages/layout/DiscoverPageLayout'
import { PageMediaGallery } from '@app/components/blocks/mediaGallery/PageMediaGallery'
import { DataCard } from './sections/DataCard'
import defaultProjectPicture from '@app/assets/defaultProjectPicture.png'
import useTransform from '@app/util/useTransform'
import { ViewInitiativeModel } from 'functions/app/initiative/view'
import { isUuid } from '@app/util/helpers'
import { useParamState } from '@app/util/hooks/useParamState'
import { match } from 'ts-pattern'
import { PageSeo } from '@app/components/core/seo/PageSeo'
import { initiativeTabs } from './sections/initiativeTabs'
import { getEntityPageStatus } from '@app/util/getEntityPageStatus'
import { NotFoundPage } from '@app/pages/error/NotFoundPage'
import { buildBreadcrumbListJsonLd } from '@app/util/seo/buildBreadcrumbListJsonLd'
import { shouldNoindexPageTab } from '@app/util/seo/shouldNoindexPageTab'
import { useRootDomain } from '@app/util/hooks/useRootDomain'
import { pages } from '@app/util/links'

export type InitiativePageProps = {
  initiativeId: string
}

export const InitiativePage = ({ initiativeId }: InitiativePageProps) => {
  const isMobile = useMediaQuery()
  const translations = useTranslations().app
  const transformStyle = useTransform()

  const queryPayload = useMemo(() => {
    return isUuid(initiativeId) ? { id: initiativeId } : { slug: initiativeId }
  }, [initiativeId])

  const initiativeQuery = useQuery('view-initiative', queryPayload)
  const initiative: ViewInitiativeModel | null = initiativeQuery.data

  const pageStatus = getEntityPageStatus(initiativeQuery.loading, initiative)

  if (pageStatus === 'not-found') {
    return <NotFoundPage />
  }

  if (pageStatus === 'loading') {
    return (
      <PageLayout gap="2rem">
        <PageSeo />
        <Column
          gap="1.5rem"
          alignItems="stretch"
          style={{ marginBottom: '90vh' }}
        >
          <PageBreadcrumbs
            level2={translations.pageNames.initiatives}
            level3="Loading..."
          />
        </Column>
      </PageLayout>
    )
  }

  if (isMobile) {
    return (
      <PageLayout gap="0rem">
        <PageHead initiative={initiative} />
        <Column alignItems="stretch" gap="1.5rem">
          <Column>
            <Title.Page styledAsLevel={3}>{initiative.title}</Title.Page>
            <Text.Medium>{initiative.description}</Text.Medium>
          </Column>
          <PageMediaGallery
            title={initiative.title}
            images={initiative.images}
            defaultImage={defaultProjectPicture}
          />
          <DataCard initiative={initiative} />
        </Column>
        <TabContent initiative={initiative} />
      </PageLayout>
    )
  }

  return (
    <PageLayout gap="3.5rem">
      <PageHead initiative={initiative} />
      <Column gap="2rem" alignItems="stretch" style={transformStyle}>
        <PageBreadcrumbs
          level2={translations.pageNames.initiatives}
          level3={initiative.title}
        />
        <Title.Page styledAsLevel={1} style={{ paddingRight: '24rem' }}>
          {initiative.title}
        </Title.Page>
        <Subtitle style={{ paddingRight: '24rem' }}>
          {initiative.description}
        </Subtitle>
        <DiscoverPageLayout sidebar={<RightColumn initiative={initiative} />}>
          <PageMediaGallery
            title={initiative.title}
            images={initiative.images}
            defaultImage={defaultProjectPicture}
            style={{ width: '100%' }}
          />
          <TabContent initiative={initiative} />
        </DiscoverPageLayout>
      </Column>
    </PageLayout>
  )
}

const PageHead = ({ initiative }: { initiative: ViewInitiativeModel }) => {
  const appTranslations = useTranslations().app
  const translations = appTranslations.initiativePage
  const { pageNames } = appTranslations
  const rootDomain = useRootDomain()
  const [tab] = useParamState(initiativeTabs)
  const tabTitle = match(tab)
    .with(initiativeTabs.overview, () => '')
    .with(initiativeTabs.discussion, () => translations.thread)
    .with(initiativeTabs.events, () => translations.events)
    .exhaustive()
  const initiativeUrl = `${rootDomain}${pages.initiative(initiative.slug)}`
  const breadcrumbJsonLd = buildBreadcrumbListJsonLd([
    { name: pageNames.home, url: `${rootDomain}${pages.home}` },
    { name: pageNames.initiatives, url: `${rootDomain}${pages.initiatives}` },
    { name: initiative.title, url: initiativeUrl },
  ])
  return (
    <PageSeo
      pageTitle={initiative.title}
      tabTitle={tabTitle}
      description={initiative.description}
      meta={{
        metaTitle: initiative.metaTitle,
        metaDescription: initiative.metaDescription,
      }}
      image={initiative.images?.[0]}
      jsonLd={breadcrumbJsonLd}
      noindex={shouldNoindexPageTab(tab)}
    />
  )
}
